import { useState, useCallback } from "react";
import useUsers from "hooks/userManagement/useUsers";
import { useAuth } from "contexts/AuthContext";
import apiClient from "utils/apiClient";

import {
  PROFILE_SERVICE_BASE_URL,
  STORAGE_BASE_URL,
  ACTOR_ID_HEADER_KEY,
  CLIENT_NAME_HEADER_KEY,
  CLIENT_NAME_HEADER_VALUE,
} from "../constants";

const PROFILE_BASE_URL = PROFILE_SERVICE_BASE_URL;

const IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".heic"];
const DOCUMENT_EXTS = [".pdf", ".doc", ".docx", ".odt", ".rtf"];
const SPREADSHEET_EXTS = [".xls", ".xlsx", ".csv", ".ods"];
const PRESENTATION_EXTS = [".ppt", ".pptx", ".odp"];
const ARCHIVE_EXTS = [".zip", ".rar", ".7z", ".tar", ".gz"];
const CODE_EXTS = [".js", ".jsx", ".ts", ".tsx", ".java", ".py", ".html", ".css", ".json", ".xml"];
const ACCEPTED_STORAGE_FILE_TYPES = new Set([
  "ARCHIVE",
  "AUDIO",
  "CODE",
  "CONFIG",
  "DATABASE",
  "DOCUMENT",
  "EXECUTABLE",
  "IMAGE",
  "OTHER",
  "PHOTO",
  "PRESENTATION",
  "SPREADSHEET",
  "TEXT",
  "VIDEO",
]);
const FILE_IDS_NOT_FOUND_PATTERN = /files?\s+with\s+fileids?.*not found/i;
const FILE_NOT_READY_MESSAGE =
  "Some uploaded files are not available yet. Please wait a few seconds and try again.";
const DEFAULT_ERROR_MESSAGE =
  "Something went wrong while processing the file request.";

function isImage(filename = "") {
  const lower = filename.toLowerCase();
  return IMAGE_EXTS.some((ext) => lower.endsWith(ext));
}

function guessContentType(filename = "") {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".heic")) return "image/heic";
  return "image/jpeg";
}

function hasExtension(filename = "", extensions = []) {
  const lower = filename.toLowerCase();
  return extensions.some((ext) => lower.endsWith(ext));
}

function storageFileTypeFromMime(mimeType = "") {
  const normalized = String(mimeType || "").toLowerCase();
  if (!normalized.includes("/")) return "";
  if (normalized.startsWith("image/")) return "PHOTO";
  if (normalized.startsWith("video/")) return "VIDEO";
  if (normalized.startsWith("audio/")) return "AUDIO";
  if (normalized.startsWith("text/")) return "TEXT";
  if (normalized.includes("spreadsheet") || normalized.includes("excel") || normalized.includes("csv")) return "SPREADSHEET";
  if (normalized.includes("presentation") || normalized.includes("powerpoint")) return "PRESENTATION";
  if (normalized.includes("zip") || normalized.includes("compressed") || normalized.includes("archive")) return "ARCHIVE";
  if (normalized.includes("json") || normalized.includes("xml") || normalized.includes("javascript")) return "CODE";
  if (normalized.includes("pdf") || normalized.includes("word") || normalized.includes("document") || normalized.includes("rtf")) {
    return "DOCUMENT";
  }
  return "OTHER";
}

function storageFileTypeFromFilename(filename = "") {
  if (isImage(filename)) return "PHOTO";
  if (hasExtension(filename, DOCUMENT_EXTS)) return "DOCUMENT";
  if (hasExtension(filename, SPREADSHEET_EXTS)) return "SPREADSHEET";
  if (hasExtension(filename, PRESENTATION_EXTS)) return "PRESENTATION";
  if (hasExtension(filename, ARCHIVE_EXTS)) return "ARCHIVE";
  if (hasExtension(filename, CODE_EXTS)) return "CODE";
  if (hasExtension(filename, [".txt", ".md"])) return "TEXT";
  if (hasExtension(filename, [".mp4", ".mov", ".avi", ".webm"])) return "VIDEO";
  if (hasExtension(filename, [".mp3", ".wav", ".m4a", ".aac"])) return "AUDIO";
  return "";
}

function normalizeStorageFileType(fileType, filename = "") {
  const normalized = String(fileType || "").trim().toUpperCase();
  if (normalized && ACCEPTED_STORAGE_FILE_TYPES.has(normalized)) return normalized;
  if (normalized === "FILE") return storageFileTypeFromFilename(filename) || "DOCUMENT";

  const fromMime = storageFileTypeFromMime(fileType);
  if (fromMime) return fromMime;

  return storageFileTypeFromFilename(filename) || "OTHER";
}

function getApiErrorMessage(err) {
  const responseMessage = err?.response?.data?.message;
  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage.trim();
  }
  if (typeof err?.message === "string" && err.message.trim()) {
    return err.message.trim();
  }
  return DEFAULT_ERROR_MESSAGE;
}

function isMissingFileIdsError(err) {
  const status = err?.response?.status ?? err?.response?.data?.code;
  const message = err?.response?.data?.message;
  return (
    Number(status) === 404 &&
    typeof message === "string" &&
    FILE_IDS_NOT_FOUND_PATTERN.test(message)
  );
}

function toReadableUploadError(err, fallbackMessage = DEFAULT_ERROR_MESSAGE) {
  const resolvedMessage = isMissingFileIdsError(err)
    ? FILE_NOT_READY_MESSAGE
    : getApiErrorMessage(err) || fallbackMessage;

  const readableError = new Error(resolvedMessage);
  readableError.status = err?.response?.status ?? err?.response?.data?.code;
  readableError.apiMessage = err?.response?.data?.message;
  readableError.cause = err;
  return readableError;
}

function normalizeWorkAuthorizationPayload(
  workAuthorizationInput,
  idDocumentIdsInput
) {
  const isObjectPayload =
    workAuthorizationInput &&
    typeof workAuthorizationInput === "object" &&
    !Array.isArray(workAuthorizationInput);

  const payload = isObjectPayload
    ? { ...workAuthorizationInput }
    : {
        workAuthorizationType: workAuthorizationInput,
        type: workAuthorizationInput,
      };

  if (Array.isArray(idDocumentIdsInput)) {
    payload.idDocumentIds = idDocumentIdsInput;
  }

  const normalizedType = payload.type || payload.workAuthorizationType || "";
  const normalizedCapability =
    typeof payload.capability === "string"
      ? payload.capability.trim().toUpperCase()
      : payload.capability;

  return {
    ...payload,
    type: normalizedType,
    workAuthorizationType: payload.workAuthorizationType || normalizedType,
    capability: normalizedCapability,
    notes:
      typeof payload.notes === "string" && payload.notes.trim()
        ? payload.notes.trim()
        : payload.notes || null,
  };
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function withMissingFileRetry(fn, options = {}) {
  const { retries = 2, delayMs = 1000 } = options;
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      const shouldRetry = isMissingFileIdsError(err) && attempt < retries;
      if (!shouldRetry) throw err;
      await sleep(delayMs * (attempt + 1));
    }
  }

  throw lastError;
}

export async function compressImageToTarget(file, opts = {}) {
  const {
    maxBytes = 500_000,
    maxWidth = 1920,
    maxHeight = 1920,
    initialQuality = 0.8,
    minQuality = 0.5,
    stepQuality = 0.1,
    scaleStep = 0.9,
    forceJpeg = true,
  } = opts;

  const getBlob = (canvas, quality) =>
    new Promise((resolve) => {
      canvas.toBlob(resolve, forceJpeg ? "image/jpeg" : file.type, quality);
    });

  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = async () => {
      try {
        let width = img.width;
        let height = img.height;
        const aspect = width / height;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            width = maxWidth;
            height = Math.floor(width / aspect);
          } else {
            height = maxHeight;
            width = Math.floor(height * aspect);
          }
        }

        let quality = initialQuality;

        const draw = () => {
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          return canvas;
        };

        let canvas = draw();
        let blob = await getBlob(canvas, quality);
        let size = blob.size;

        while (
          size > maxBytes &&
          (quality > minQuality || width > 640 || height > 640)
        ) {
          if (quality > minQuality) {
            quality = Math.max(minQuality, quality - stepQuality);
          } else {
            width = Math.floor(width * scaleStep);
            height = Math.floor(height * scaleStep);
          }
          canvas = draw();
          blob = await getBlob(canvas, quality);
          size = blob.size;
        }

        if (size > file.size) {
          resolve({ blob: file, size: file.size, contentType: file.type });
        } else {
          resolve({
            blob,
            size,
            contentType: forceJpeg ? "image/jpeg" : file.type,
          });
        }
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };
    img.src = objectUrl;
  });
}

export function useFileUpload() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [meta, setMeta] = useState(null);
  const { getUserById } = useUsers(0, 20, null, true, { autoFetch: false });
  const { user } = useAuth();

  const token = sessionStorage.getItem("authToken");

  const createUploadIntent = useCallback(
    async (
      userId,
      filename,
      fileSize,
      purpose = "USER_PROFILE",
      fileType = "PHOTO",
      options = {}
    ) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (userId && !options.skipUserValidation) {
        const existingUser = await getUserById(userId);
        if (!existingUser || !existingUser.id)
          throw new Error("User not found for uploading");
      }

      const normalizedFileType = normalizeStorageFileType(fileType, filename);
      const body = { filename, fileType: normalizedFileType, size: fileSize, purpose };
      if (userId && options.includeUserId !== false) body.userId = userId;

      console.log("[useFileUpload] Step 1: creating upload intent", {
        userId,
        filename,
        fileSize,
        purpose,
        fileType: normalizedFileType,
      });

      const response = await apiClient.post(
        `${STORAGE_BASE_URL}/api/v1/files/upload-intent`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            [ACTOR_ID_HEADER_KEY]: user?.id || userId || "",
            [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
          },
        }
      );

      console.log("[useFileUpload] Step 1: upload intent created", {
        fileId: response.data?.file?.id,
        hasUploadUrl: Boolean(response.data?.uploadUrl),
      });

      return response.data;
    },
    [token, getUserById, user?.id]
  );

  const uploadToPresignedUrl = useCallback(
    async (blob, uploadUrl, contentType) => {
      console.log("[useFileUpload] Step 2: uploading blob to presigned URL", {
        blobSize: blob.size,
        contentType,
      });

      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType || "application/octet-stream" },
        body: blob,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error("[useFileUpload] Step 2: presigned upload FAILED", {
          status: res.status,
          text,
        });
        throw new Error(`Presigned upload failed: ${res.status} ${text}`);
      }

      console.log("[useFileUpload] Step 2: presigned upload SUCCESS", {
        status: res.status,
      });
      return true;
    },
    []
  );

  const confirmFileUpload = useCallback(
    async (fileIdToConfirm, actorId) => {
      if (!fileIdToConfirm || !actorId || !token) {
        console.warn("[useFileUpload] Step 3: confirmUpload SKIPPED", {
          fileId: fileIdToConfirm,
          actorId,
          hasToken: Boolean(token),
        });
        return false;
      }

      console.log("[useFileUpload] Step 3: confirming upload", {
        fileId: fileIdToConfirm,
        actorId,
      });

      try {
        const res = await apiClient.patch(
          `${STORAGE_BASE_URL}/api/v1/files/${fileIdToConfirm}/confirm-upload`,
          null,
          {
            headers: {
              [ACTOR_ID_HEADER_KEY]: actorId,
              Authorization: `Bearer ${token}`,
              [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
            },
          }
        );
        console.log("[useFileUpload] Step 3: confirmUpload SUCCESS", {
          fileId: fileIdToConfirm,
          actorId,
          status: res?.status,
          data: res?.data,
        });
        return true;
      } catch (err) {
        console.warn("[useFileUpload] Step 3: confirmUpload FAILED", {
          fileId: fileIdToConfirm,
          actorId,
          error: err?.message,
          status: err?.response?.status,
          responseData: err?.response?.data,
        });
        return false;
      }
    },
    [token]
  );

  const getFileMetadata = useCallback(
    async (fileIdToFetch) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!fileIdToFetch) throw new Error("fileId is required");
      try {
        const res = await apiClient.get(
          `${STORAGE_BASE_URL}/api/v1/files/${fileIdToFetch}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
            },
          }
        );
        return res.data;
      } catch (err) {
        console.warn(
          "[useFileUpload] getFileMetadata failed",
          fileIdToFetch,
          err?.response?.status
        );
        throw toReadableUploadError(err, "Failed to fetch file metadata.");
      }
    },
    [token]
  );

  const getFileSignedUrl = useCallback(
    async (fileIdToFetch) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!fileIdToFetch) throw new Error("fileId is required");
      try {
        const res = await apiClient.get(
          `${STORAGE_BASE_URL}/api/v1/files/photo/signed-url`,
          {
            params: { fid: fileIdToFetch },
            headers: {
              Authorization: `Bearer ${token}`,
              [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
            },
          }
        );
        if (typeof res.data === "string") return res.data;
        return res.data?.url || res.data?.signedUrl || res.data?.downloadUrl || "";
      } catch (err) {
        console.warn(
          "[useFileUpload] getFileSignedUrl failed",
          fileIdToFetch,
          err?.response?.status
        );
        throw toReadableUploadError(err, "Failed to fetch signed file URL.");
      }
    },
    [token]
  );

  const submitIdentityVerification = useCallback(
    async (userId, identificationDocumentType, idDocumentIds) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!userId) throw new Error("userId is required");
      if (!identificationDocumentType)
        throw new Error("identificationDocumentType is required");
      if (
        !idDocumentIds ||
        !Array.isArray(idDocumentIds) ||
        idDocumentIds.length === 0
      )
        throw new Error("idDocumentIds must be a non-empty array");

      const body = { idDocumentIds, identificationDocumentType };

      try {
        const res = await withMissingFileRetry(() =>
          apiClient.post(
            `${PROFILE_BASE_URL}/api/v1/user-profiles/identity-verification/user/${userId}`,
            body,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
                "actor-id": user?.id || "",
              },
            }
          )
        );
        return res.data;
      } catch (err) {
        console.error("[useFileUpload] submitIdentityVerification failed", err);
        throw toReadableUploadError(
          err,
          "Failed to submit identity verification documents."
        );
      }
    },
    [token, user?.id]
  );

  const submitWorkAuthorization = useCallback(
    async (userId, workAuthorizationData, idDocumentIds) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!userId) throw new Error("userId is required");
      const body = normalizeWorkAuthorizationPayload(
        workAuthorizationData,
        idDocumentIds
      );
      if (!body.type && !body.workAuthorizationType)
        throw new Error("workAuthorizationType is required");
      if (
        !body.idDocumentIds ||
        !Array.isArray(body.idDocumentIds) ||
        body.idDocumentIds.length === 0
      )
        throw new Error("idDocumentIds must be a non-empty array");

      try {
        const res = await withMissingFileRetry(() =>
          apiClient.post(
            `${PROFILE_BASE_URL}/api/v1/user-profiles/work-authorization/user/${userId}`,
            body,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
                "actor-id": user?.id || "",
              },
            }
          )
        );
        return res.data;
      } catch (err) {
        console.error("[useFileUpload] submitWorkAuthorization failed", err);
        throw toReadableUploadError(
          err,
          "Failed to submit work authorization documents."
        );
      }
    },
    [token, user?.id]
  );

  const uploadFile = useCallback(
    async (userId, file, options = {}) => {
      console.log("[useFileUpload] ===== UPLOAD START =====", {
        userId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      setUploading(true);
      setError(null);

      try {
        let uploadBlob = file;
        let contentType = file.type || guessContentType(file.name);
        let fileSize = file.size;

        if (isImage(file.name) && !options.preserveOriginal) {
          const compressed = await compressImageToTarget(file, {
            forceJpeg: false,
          });
          uploadBlob = compressed.blob;
          fileSize = compressed.size;
          contentType = compressed.contentType;
          console.log("[useFileUpload] Image compressed", {
            originalSize: file.size,
            newSize: fileSize,
            contentType,
          });
        }

        const intent = await createUploadIntent(
          userId,
          file.name,
          fileSize,
          options.purpose,
          options.fileType,
          options
        );

        if (!intent.uploadUrl) {
          throw new Error("No uploadUrl in upload intent response");
        }
        if (!intent.file?.id) {
          throw new Error("No file.id in upload intent response");
        }

        await uploadToPresignedUrl(uploadBlob, intent.uploadUrl, contentType);

        const shouldConfirmUpload = options.confirmUpload !== false;
        const confirmed = shouldConfirmUpload
          ? await confirmFileUpload(intent.file.id, userId || user?.id)
          : false;

        const filenameFromFile = intent.file?.filename;
        const metaToSet = filenameFromFile
          ? { filename: filenameFromFile }
          : { filename: file.name };

        setFileId(intent.file.id);
        setMeta(metaToSet);

        console.log("[useFileUpload] ===== UPLOAD COMPLETE =====", {
          fileId: intent.file.id,
          confirmed,
        });

        return {
          fileId: intent.file.id,
          meta: metaToSet,
          file: intent.file || null,
          confirmed,
        };
      } catch (err) {
        console.error("[useFileUpload] ===== UPLOAD FAILED =====", err);
        const readableError = toReadableUploadError(
          err,
          "Failed to upload file."
        );
        setError(readableError.message);
        throw readableError;
      } finally {
        setUploading(false);
      }
    },
    [createUploadIntent, uploadToPresignedUrl, confirmFileUpload, user?.id]
  );

  return {
    uploadFile,
    confirmFileUpload,
    uploading,
    error,
    fileId,
    meta,
    getFileMetadata,
    getFileSignedUrl,
    submitIdentityVerification,
    submitWorkAuthorization,
  };
}
